// src/App.js
import React, { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import AppRouter from "./router/Router";
import "./App.css";
import { CustomThemeProvider } from "./context/ThemeContext";
import MainLayout from "./layout/MainLayout";
import { url } from "./constants/url";
import { WebSocketProvider } from "./context/WebSocketContext";
import { NotificationProvider } from "./context/NotificationContext";

// Material-UI imports
import { Box, CircularProgress, Typography } from "@mui/material";

// Simple Circular Loader with #050712 color
const SimpleLoader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        gap: 3
      }}
    >
      {/* Circular Progress with #050712 color */}
      <CircularProgress 
        size={50} 
        thickness={4}
        sx={{
          color: '#050712'
        }}
      />
      
      {/* Loading Text */}
      <Typography 
        variant="body1" 
        sx={{
          color: '#050712',
          fontWeight: 500
        }}
      >
        Loading...
      </Typography>
    </Box>
  );
};

function AppContent() {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [firebaseToken, setFirebaseToken] = useState(null);

  const fetchUserData = async () => {
    try {
      if (!currentUser) {
        setUserData(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      const response = await fetch(
        `${url}/api/users/fetch-user?email=${currentUser.email}`
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Fetched User Data in App:", data);
        if (data && Object.keys(data).length > 0) {
          setUserData(data);
        } else {
          setUserData(null);
        }
      } else {
        console.error("❌ Failed to fetch user data");
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  // Get Firebase ID token
  useEffect(() => {
    const getFirebaseToken = async () => {
      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          console.log("🔥 Firebase token obtained for WebSocket");
          setFirebaseToken(token);
        } catch (error) {
          console.error("Error getting Firebase token:", error);
          setFirebaseToken(null);
        }
      } else {
        setFirebaseToken(null);
      }
    };

    getFirebaseToken();
  }, [currentUser]);

  // Refresh token every hour
  useEffect(() => {
    if (!currentUser) return;

    const refreshTokenInterval = setInterval(async () => {
      try {
        const token = await currentUser.getIdToken(true);
        console.log("🔄 Firebase token refreshed");
        setFirebaseToken(token);
      } catch (error) {
        console.error("Error refreshing Firebase token:", error);
      }
    }, 55 * 60 * 1000);

    return () => clearInterval(refreshTokenInterval);
  }, [currentUser]);

  useEffect(() => {
    fetchUserData();
  }, [currentUser]);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().then(permission => {
        console.log('🔔 Notification permission:', permission);
      });
    }
  }, []);

  // Show loading spinner
  if (loading) {
    return <SimpleLoader />;
  }

  return (
    <CustomThemeProvider>
      <WebSocketProvider userData={userData} token={firebaseToken}>
        <NotificationProvider userId={userData?._id}>
          {currentUser ? (
            <MainLayout userData={userData}>
              <AppRouter userData={userData} />
            </MainLayout>
          ) : (
            <AppRouter />
          )}
        </NotificationProvider>
      </WebSocketProvider>
    </CustomThemeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;